import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators';
import store from '@/store';
import { ClientUtil } from '@xl/js-utils';
import ua from '@xl/user-agent';
import {
  fetchCityList,
  fetchOpenJoinOfCity,
  fetchProvinceCityData,
} from '@/api/modules/config';
import { getIpPosition } from '@/api/modules/join';
import watchdog from '@xl/watch-dog-jsutil';
import sensors from '@/common/sensors';
import { Toast } from 'vant';
import { getGpsPosition } from '@/common/tools';
import { IGetRulesData, IRules } from '@/api/modules/register/types';
const { is_xl_driver_app: isInApp } = ua.parse();

export interface IRegisterCity {
  provinceEn?: string; // 省份拼音
  provinceName?: string;
  cityId?: string;
  cityName?: string;
  isOpen?: boolean;
}

export interface IProvinceCityData {
  provinceNameCn: string;
  provinceNameEn: string; // 省份拼音名
  cityList?: {
    id: string;
    nameCn: string;
  }[];
}

export interface IRegisterState {
  registerCityObj: IRegisterCity;
  provinceCityData: IProvinceCityData[];
  cityList: Record<string, Record<string, string>> | null;
  regStatus: number;
  encode_driver_id: string;
  highest_driver_id: string;
  firstUrl: string;
}

@Module({ dynamic: true, store, name: 'register' })
class Register extends VuexModule implements IRegisterState {
  public registerCityObj: IRegisterCity = {
    provinceEn: '',
    provinceName: '',
    cityId: '',
    cityName: '',
    isOpen: true,
  };

  public cityList: Record<string, Record<string, string>> | null = null;
  public provinceCityData: IProvinceCityData[] = [];
  public regStatus = 0;
  public encode_driver_id = '';
  public highest_driver_id = '';
  public firstUrl = '';

  // 准入规则说明
  public joinRequires: IGetRulesData['principle'] | null = null;

  public joinRules: IRules | null = null;

  @Mutation
  public SET_REGISTER_CITY_OBJ(registerCityObj: IRegisterCity) {
    this.registerCityObj = registerCityObj;
  }

  @Mutation
  private SET_CITY_LIST(cityList: Record<string, Record<string, string>>) {
    this.cityList = cityList;
  }

  @Mutation
  public SET_PROVINCE_CITY_DATA(provinceCity: IProvinceCityData[]) {
    this.provinceCityData = provinceCity;
  }

  @Mutation
  public SET_REG_STATUS(regStatus: number) {
    this.regStatus = regStatus;
  }

  @Mutation
  public SET_ENCODE_DRIVER_ID(driverId: string) {
    this.encode_driver_id = driverId;
  }

  @Mutation
  public SET_HIGHEST_ENCODE_DRIVER_ID(driverId: string) {
    this.highest_driver_id = driverId;
  }

  @Mutation
  public SET_FIRST_URL(url: string) {
    this.firstUrl = url;
  }

  @Mutation
  public SET_JOIN_REQUIRES(requires: IGetRulesData['principle']) {
    this.joinRequires = requires;
  }

  @Mutation
  public SET_JOIN_RULES(rules: IRules) {
    this.joinRules = rules;
  }

  @Action
  public async InitCityList() {
    if (!this.cityList) {
      const { data } = await fetchCityList();
      this.SET_CITY_LIST(data);
    }

    return this.cityList;
  }

  // 获取省市级联信息
  @Action
  public async InitProvinceCityData() {
    if (!this.provinceCityData.length) {
      const { data } = await fetchProvinceCityData();
      this.SET_PROVINCE_CITY_DATA(data);
    }

    return this.provinceCityData;
  }

  @Action
  public async GetCityIsOpen({ cityId }: { cityId?: string }) {
    if (cityId === undefined) {
      return false;
    }

    const res = await fetchOpenJoinOfCity({
      city_id: cityId,
    });

    const isOpen = res.data.is_open;
    return isOpen;
  }

  @Action
  public async InitPosition({ loading = true }: { loading?: boolean } = {}) {
    if (loading) {
      Toast.loading({
        message: '定位中...',
        duration: 0,
        forbidClick: true,
      });
    }

    // 通用获取城市列表
    const cityList = await this.InitCityList();
    // 省市级联数据
    const provinceCity = await this.InitProvinceCityData();

    const isAndroid = ClientUtil.isAndroid();
    const revision = ClientUtil.getAppClientVersion();

    // 因为 webcall 回调是异步的，将它包装成 Promise，便于业务代码中处理异步
    const webcallPromise = () =>
      new Promise((resolve, reject) => {
        // 在司机app的ios端getCityInfo的回调方法名是写死为cityInfoCallback的，所以必须要用下面这种方式去设置回调方法
        window.cityInfoCallback = async(cityInfo) => {
          watchdog.sendEvent(
            'webcall.callback',
            'getCityInfo',
            JSON.stringify(cityInfo),
          );
          const cityObject: IRegisterCity = {};
          console.log(cityInfo, 'cityInfoCallback');

          const gpsCityName = cityInfo.cityName;
          const gpsDistrict = cityInfo.district;
          if (gpsCityName) {
            for (const key in cityList) {
              const cityName = cityList[key]?.name;
              // 匹配到城市
              if (gpsCityName.includes(cityName)) {
                cityObject.cityId = key;
                cityObject.cityName = cityName;
                break;
              }
            }
            console.log(cityObject);

            // 获取城市是否开通
            const isOpen = await this.GetCityIsOpen({
              cityId: cityObject.cityId,
            });

            cityObject.isOpen = isOpen;
            console.log(isOpen);

            // cityId 获取所属省份
            for (const province of this.provinceCityData) {
              for (const city of province.cityList!) {
                if (String(city.id) === cityObject.cityId) {
                  cityObject.provinceName = province.provinceNameCn;
                  cityObject.provinceEn = province.provinceNameEn; // 省份拼音
                  break;
                }
              }
              if (cityObject.provinceName) {
                break;
              }
            }

            this.SET_REGISTER_CITY_OBJ(cityObject);

            return resolve(undefined);
          } else {
            Toast.clear();
            Toast.fail('定位失败，请手动选择抢单城市');
            return reject(new Error());
          }
        };

        // 获取位置
        ClientUtil.webcall({
          // 注：安卓APP里由于隐私合规原因，老的 getCityInfo 已无法在登录阶段获取到城市信息
          action: (isAndroid && revision >= 1323
            ? 'getAndroidCityInfo'
            : 'getCityInfo') as any,
          callback: 'cityInfoCallback',
        });

        // action 超时兜底
        setTimeout(() => {
          Toast.clear();
          reject(new Error());
        }, 5000);
      });

    // 1036 版本之后才支持
    if (isInApp && revision >= 1036) {
      await webcallPromise();
    } else {
      // 端外
      const cityObject: {
        provinceEn?: string;
        provinceName?: string;
        cityId?: string;
        cityName?: string;
        isOpen?: boolean;
      } = {};
      let gpsInfo: any = {};

      const { client_type, location_city_id } = (store.state as any).app
        .urlParams;

      if (client_type === 'xl_wxa') {
        /**
         * @log wx_register_url_city_id
         * @name wx_register_url_city_id
         * @description 小程序 city id
         * @route ['/register','/register_v2']
         * @param location_city_id - unknown
         */
        sensors.track('wx_register_url_city_id', {
          location_city_id,
        });
        if (+location_city_id) {
          cityObject.cityId = location_city_id;
          cityObject.cityName = cityList?.[location_city_id]?.name;
        } else {
          Toast.clear();
          Toast.fail('定位失败，请手动选择抢单城市');
        }
      } else {
        gpsInfo = await getGpsPosition();
        console.log(gpsInfo, 'gpsInfo');
        const gpsCityName = gpsInfo.addressComponents.city;

        if (gpsInfo.addressComponents && gpsInfo.addressComponents.city) {
          for (const key in cityList) {
            const cityName = cityList[key].name;
            // 匹配到城市
            if (gpsCityName.includes(cityName)) {
              cityObject.cityId = key;
              cityObject.cityName = cityName;
              break;
            }
          }
        } else {
          Toast.clear();
          Toast.fail('定位失败，请手动选择抢单城市');
        }
      }

      // 获取城市是否开通
      const isOpen = await this.GetCityIsOpen({
        cityId: cityObject.cityId,
      });

      cityObject.isOpen = isOpen;
      console.log(isOpen);

      // cityId 获取所属省份
      for (const province of this.provinceCityData) {
        for (const city of province.cityList!) {
          if (String(city.id) === cityObject.cityId) {
            cityObject.provinceName = province.provinceNameCn;
            cityObject.provinceEn = province.provinceNameEn; // 省份拼音
            break;
          }
        }
        if (cityObject.provinceName) {
          break;
        }
      }

      this.SET_REGISTER_CITY_OBJ(cityObject);
    }

    Toast.clear();
  }

  @Action
  public async IpPosition() {
    try {
      const { data } = await getIpPosition();
      this.initCity(data.cityId);

      window.sessionStorage.setItem('ipCity', data.cityId as string); // 埋点用
    } catch (error) {
      console.warn('IP', error);
    }
  }

  @Action
  public async initCity(city_id: string) {
    // 省市级联数据
    await this.InitCityList();
    const provinceCity = await this.InitProvinceCityData();
    const cityObject: IRegisterCity = {};

    // cityId 获取城市、省份信息
    for (const province of provinceCity) {
      for (const city of province.cityList!) {
        if (city.id == city_id) {
          cityObject.cityId = city.id;
          cityObject.cityName = city.nameCn;
          cityObject.provinceName = province.provinceNameCn;
          cityObject.provinceEn = province.provinceNameEn;
          break;
        }
      }
      if (cityObject.provinceName) {
        break;
      }
    }
    this.SET_REGISTER_CITY_OBJ(cityObject);
  }
}

export const RegisterModule = getModule(Register);
